import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { useAssistant } from '../../contexts/AssistantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { useNotification } from '../../contexts/NotificationContext';
import { openJoinInvite } from '../../core/navigation/joinInviteController';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  isIncomingInviteNotificationId,
  listNotificationsWithIncomingInvite,
  markNotificationRead,
  type AppNotification,
} from '../../services/notificationService';
import { subscribeNotificationsChanged } from '../../services/notificationsBus';
import { GlassButton } from '../ui/GlassButton';
import { BellIcon } from '../ui/icons/BellIcon';

/**
 * In-app notification bell — tap invite to join, insights to open floating AI.
 * Push on/off lives in the panel so guests are never prompted at launch.
 */
export function NotificationBell() {
  const { isAuthenticated } = useAuth();
  const { openAssistant } = useAssistant();
  const { refresh: refreshHousehold } = useHouseholdMembership();
  const {
    devicePushOn,
    permissionStatus,
    enablePush,
    disablePush,
    refreshPermission,
  } = useNotification();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [pushBusy, setPushBusy] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setUnreadCount(0);
      return;
    }
    try {
      const res = await listNotificationsWithIncomingInvite();
      setItems(res.notifications);
      setUnreadCount(res.unreadCount);
      const joined = res.notifications.some(
        (n) => !n.readAt && n.data?.event === 'member_joined',
      );
      if (joined) {
        void refreshHousehold();
      }
    } catch {
      // silent
    }
  }, [isAuthenticated, refreshHousehold]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 60_000);
    const unsub = subscribeNotificationsChanged(() => void load());
    return () => {
      clearInterval(id);
      unsub();
    };
  }, [load]);

  if (!isAuthenticated) return null;

  const onTap = async (n: AppNotification) => {
    setOpen(false);
    if (!n.readAt && !isIncomingInviteNotificationId(n.id)) {
      try {
        await markNotificationRead(n.id);
        void load();
      } catch {
        // ignore
      }
    }
    const inviteToken = n.data?.token?.trim();
    if (n.type === 'household_invite' && n.data?.event === 'invite_received' && inviteToken) {
      openJoinInvite(inviteToken);
      return;
    }
    if (n.insightId) {
      void openAssistant({
        insightId: n.insightId,
        insightTitle: n.title,
        widget: 'notification',
      });
    }
  };

  const handleTogglePush = async () => {
    setPushBusy(true);
    try {
      if (devicePushOn) {
        await disablePush();
      } else {
        await enablePush();
      }
      await refreshPermission();
    } finally {
      setPushBusy(false);
    }
  };

  const blocked = permissionStatus === 'blocked';
  const isWeb = Platform.OS === ('web' as typeof Platform.OS);

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}${
          devicePushOn ? '' : ', push off'
        }`}
        onPress={() => {
          setOpen(true);
          void load();
          void refreshPermission();
        }}
        style={({ pressed }) => [
          styles.bell,
          pressed && styles.pressed,
          Platform.OS === 'web' && styles.webPointer,
        ]}
      >
        <BellIcon
          color={devicePushOn ? colors.accent : colors.textMuted}
          size={18}
          muted={!devicePushOn}
        />
        {unreadCount > 0 ? (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{unreadCount > 9 ? '9+' : String(unreadCount)}</Text>
          </View>
        ) : !devicePushOn ? (
          <View style={styles.offDot} />
        ) : null}
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)}>
          <Pressable style={styles.panel} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.title}>Notifications</Text>

            <View style={styles.pushCard}>
              <View style={styles.pushCopy}>
                <Text style={styles.pushLabel}>Push alerts</Text>
                <Text style={styles.pushHint}>
                  {devicePushOn
                    ? 'On for this device — spend, insights, and household.'
                    : blocked
                      ? isWeb
                        ? 'Blocked in this browser. Use the lock icon in the address bar → Notifications → Allow.'
                        : 'Off in system settings. Enable notifications for MoneyLens, then come back.'
                      : 'Off. Enable to get spend alerts and insights on this device.'}
                </Text>
              </View>
              {blocked && !isWeb ? (
                <GlassButton
                  variant="secondary"
                  size="sm"
                  disabled={pushBusy}
                  onPress={() => {
                    void handleTogglePush();
                  }}
                >
                  {pushBusy ? <ActivityIndicator color={colors.text} /> : 'Open Settings'}
                </GlassButton>
              ) : (
                <GlassButton
                  variant="secondary"
                  size="sm"
                  selected={devicePushOn}
                  disabled={pushBusy || (blocked && isWeb)}
                  onPress={() => {
                    void handleTogglePush();
                  }}
                  accessibilityRole="switch"
                  accessibilityState={{ checked: devicePushOn }}
                  accessibilityLabel="Push alerts"
                >
                  {pushBusy ? (
                    <ActivityIndicator color={colors.text} />
                  ) : devicePushOn ? (
                    'On'
                  ) : (
                    'Off'
                  )}
                </GlassButton>
              )}
            </View>

            <ScrollView style={styles.list}>
              {items.length === 0 ? (
                <Text style={styles.empty}>No notifications yet</Text>
              ) : (
                items.map((n) => (
                  <Pressable
                    key={n.id}
                    onPress={() => void onTap(n)}
                    style={[styles.row, !n.readAt && styles.rowUnread]}
                  >
                    <Text style={styles.rowTitle}>{n.title}</Text>
                    <Text style={styles.rowBody} numberOfLines={2}>
                      {n.body}
                    </Text>
                  </Pressable>
                ))
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  bell: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
    marginRight: spacing.sm,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.warning,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  badgeText: { fontSize: 9, fontWeight: '800', color: '#0B0F14' },
  offDot: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.warning,
    borderWidth: 1,
    borderColor: '#0B0F14',
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2,6,23,0.65)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: 72,
    paddingRight: spacing.md,
  },
  panel: {
    width: 320,
    maxHeight: 460,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(11,15,20,0.96)',
    padding: spacing.md,
  },
  title: { ...typography.headline, fontSize: 16, color: colors.text, marginBottom: spacing.sm },
  pushCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    marginBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  pushCopy: { flex: 1, gap: 2 },
  pushLabel: { ...typography.caption, fontWeight: '700', color: colors.text },
  pushHint: { ...typography.caption, color: colors.textMuted, fontSize: 12, lineHeight: 16 },
  list: { maxHeight: 300 },
  empty: { ...typography.body, color: colors.textMuted, fontSize: 14 },
  row: {
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    gap: 4,
  },
  rowUnread: { backgroundColor: 'rgba(61,214,164,0.06)' },
  rowTitle: { ...typography.caption, fontWeight: '700', color: colors.text },
  rowBody: { ...typography.caption, color: colors.textMuted },
  pressed: { opacity: 0.85 },
  webPointer: { cursor: 'pointer' },
});
